import { getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type RootContainerProps } from "@client/content/types";
import { updateItem } from "@client/lib/array";
import { type WithIsEditMode } from "@client/types";
import { isNull } from "lodash";
import * as React from "react";
import CartItems from "../Items/component";
import { type Cart } from "../types";

export type MapProps = RootContainerProps &
  WithIsEditMode & {
    cart: Cart.Cart;
    fieldStyle: React.CSSProperties;
    isBusy: boolean;
    currencyCode: string;
    noImageUrl: string;
    shouldDisplayTax: boolean;
  };
export interface ActionProps {
  onChange: (cart: Cart.Cart) => void;
  onCheckout: () => void;
  onDelete: (productId: string) => void;
}
export type Props = ActionProps & MapProps;

export default (props: Props) => {
  const onDelete = React.useCallback(
    (id: string) => {
      props.onChange({
        ...props.cart,
        items: props.cart.items.filter((x) => x.id !== id)
      });

      props.onDelete(id);
    },
    [props.onChange, props.onDelete, props.cart]
  );

  const onQuantityChange = React.useCallback(
    (id: string, value: number) => {
      props.onChange({
        ...props.cart,
        items: updateItem(props.cart.items, id, (x) => ({ ...x, quantity: value }))
      });
    },
    [props.onChange, props.cart]
  );

  const items = React.useMemo(
    (): Cart.Api.Item[] =>
      props.cart.items.map((x) => ({
        ...x,
        // for bundles: when there're bundles with the same product variant in the children
        // e.g. A+B: A and B, A+C: A and C. When we add A+B or A+C, A should be reduced for both based on the multiplier (quantity of product variant in bundle).
        availableQuantity: isNull(x.availableQuantity)
          ? null
          : Math.min(
              ...(x.children.length
                ? x.children
                : [{ id: x.id, availableQuantity: x.availableQuantity, quantityMultiplier: 1 }]
              ).map((c) => {
                const usedQuantity = props.cart.items.reduce((ores, ox) => {
                  const multplier = ox.children.find((oxc) => oxc !== c && oxc.id === c.id)?.quantityMultiplier ?? 0;
                  return ores + multplier * ox.quantity;
                }, 0);

                return Math.floor((c.availableQuantity - usedQuantity) / c.quantityMultiplier);
              })
            )
      })),
    [props.cart.items]
  );

  const hasUnavailableItems = React.useMemo(
    () => items.some((x) => isNull(x.availableQuantity) || x.availableQuantity < x.quantity),
    [items]
  );

  return (
    <div>
      <div className="wsc_cart_items">
        <CartItems
          redirectOnClick={!props.isEditMode}
          imageSize={64}
          currencyCode={props.currencyCode}
          isBusy={props.isBusy}
          items={items}
          noImageUrl={props.noImageUrl}
          fieldStyle={props.fieldStyle}
          onDelete={onDelete}
          onQuantityChange={onQuantityChange}
          shouldDisplayTax={props.shouldDisplayTax}
        />
      </div>

      <div className="footer">
        <ModuleButton
          {...getRootContainerProps(props)}
          isDisabled={props.isBusy || hasUnavailableItems}
          onClick={props.onCheckout}
        />
      </div>
    </div>
  );
};
